import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import useClickOutside from '../../hooks/useClickOutside';
import { ButtonV3 } from '../ButtonV3';
import { BoxDropdownTriggerProps } from '../Form/common/DropdownTrigger/BoxDropdownTrigger';
import { CustomDropdownTriggerProps } from '../Form/common/DropdownTrigger/CustomDropdownTrigger';
import { DropdownTrigger } from '../Form/common/DropdownTrigger/DropdownTrigger';
import { CommonTriggerProps } from '../Form/common/DropdownTrigger/types';
import { Asterisk, FormLabel } from '../Form/form.styles';
import Styled from './ChipPanel.styles';

type ChipPanelTriggerProps = {
  /** For a chip trigger (customTrigger and boxTrigger not provided) include a caret on the chip */
  withCaret?: boolean;
  /** For a chip trigger (customTrigger and boxTrigger not provided) suppress the border */
  noBorder?: boolean;

  /** Optional element to use to trigger the dropdown */
  customTrigger?: Omit<CustomDropdownTriggerProps, keyof CommonTriggerProps>;
  /**
   * Render the trigger in the same style as a dropdown
   *
   * @example
   * boxTrigger: {
   *    placeholder: "Please make a selection",
   * }
   *
   * @see BoxDropdownTrigger
   */
  boxTrigger?: Omit<BoxDropdownTriggerProps, keyof CommonTriggerProps>;
};

export type ChipPanelProps = ChipPanelTriggerProps & {
  id: string;

  /**
   * Optional classname applied to the outermost HTML element of the component.
   */
  className?: string;

  /**
   * The label _of the chip_ used to trigger the dropdown.  For a label _above_ the element
   * please use formLabel;
   */
  label: string | React.ReactElement;

  /**
   * Align the panel to the right of the chip. Default false.
   */
  alignRight?: boolean;

  /**
   * Optional label appearing above the chip/dropdown
   */
  formLabel?: {
    label: string | React.ReactElement;
    isRequired?: boolean;
  };
  /**
   * Callback when the dropdown is toggled open or closed.  Invoked after the expanded
   * state changes.  Invoked on each toggle (open or closed).
   */
  onClick?: React.MouseEventHandler<HTMLElement>;

  /**
   * In the mobile takeover, a CTA rendered at the bottom of the screen.  Useful
   * for giving mobile users a tap target instead of using the X to close the
   * take over.  Does not act as an apply button (checks to form take affect
   * immediately).
   */
  mobileCtaProps?: {
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    label: string;
  };

  /**
   * In the mobile takeover, the header at the top of the picker.
   */
  mobileHeader?: string | React.ReactNode;

  /** Disables the form element. Default: false */
  disabled?: boolean;

  /** Optional callback invoked when the panel is expanded */
  onOpen?: () => void;

  /** Optional callback invoked when the panel is collapsed */
  onClose?: () => void;

  /** Optional prop to force the expanded state */
  isOpened?: boolean;

  /** Does this get the selected (gray) style */
  selected?: boolean;
};

/**
 * Implements a dropdown panel with custom content.  The dropdown can be
 * triggered by a chip, a dropdown style box, or a custom element.  On mobile,
 * takes over the screen with an picker.
 */
export const ChipPanel: React.FC<ChipPanelProps> = ({
  children,
  className,
  disabled,
  formLabel,
  id,
  label,
  mobileCtaProps,
  mobileHeader,
  onClick,
  selected,
  isOpened = false,
  onClose,
  onOpen,

  alignRight = false,

  withCaret,
  noBorder,
  customTrigger,
  boxTrigger,
}) => {
  const [expanded, _setExpanded] = useState(isOpened);

  useEffect(() => {
    _setExpanded(isOpened);
  }, [isOpened, _setExpanded]);

  const setOpened = useCallback(
    (isOpened: boolean) => {
      _setExpanded(isOpened);

      if (isOpened && onOpen) {
        onOpen();
      }
      if (!isOpened && onClose) {
        onClose();
      }
    },
    [onOpen, onClose, _setExpanded]
  );

  const ref = useRef<HTMLDivElement>(null);
  useClickOutside({
    ref,
    onClickOutside: () => {
      setOpened(false);
    },
    isOpen: expanded,
  });

  const toggleDropdown: React.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      e.preventDefault();
      setOpened(!expanded);
      if (onClick) {
        onClick(e);
      }
    },
    [expanded, onClick, setOpened]
  );

  const handleMobileCtaClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    setOpened(false);
    if (mobileCtaProps?.onClick) {
      mobileCtaProps.onClick(e);
    }
  };

  // Determines what type of trigger will be used to open the dropdown.  Because the
  // box and custom trigger were retrofitted into this component without changing
  // the interface, the trigger props for DropdownCheckboxList are different than
  // DropdownV3.  This builds a consistent set of props to pass to the trigger
  const triggerProps = useMemo(() => {
    if (boxTrigger) {
      return { ...boxTrigger };
    }
    if (customTrigger) {
      return { ...customTrigger };
    }

    return {
      chips: true,
      selected: expanded || selected,
      label,
      withCaret,
      noBorder,
    };
  }, [expanded, selected, label, boxTrigger, customTrigger, withCaret, noBorder]);

  return (
    <Styled.ChipPanelWrapper className={className} hasLabel={Boolean(formLabel)} ref={ref}>
      {formLabel && (
        <FormLabel htmlFor={id}>
          {formLabel.label}
          {formLabel.isRequired && <Asterisk>*</Asterisk>}
        </FormLabel>
      )}

      <DropdownTrigger
        expanded={expanded}
        toggleDropdown={toggleDropdown}
        handleKeyDown={() => null}
        id={id}
        disabled={disabled}
        {...triggerProps}
      />
      {expanded && (
        <Styled.ChipPanel
          tabIndex={0}
          alignRight={alignRight}
          onClose={() => setOpened(false)}
          mobileHeader={mobileHeader || label}
          mobileCtaContent={
            mobileCtaProps && (
              <ButtonV3 fullWidth onClick={handleMobileCtaClick}>
                {mobileCtaProps?.label}
              </ButtonV3>
            )
          }
        >
          {children}
        </Styled.ChipPanel>
      )}
    </Styled.ChipPanelWrapper>
  );
};
